import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, Animated, Easing, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useAppContext } from '../contexts/AppContext';
import { useStorefront } from '../hooks/useStorefront';
import { useThemedRTLStyles } from '../hooks/useThemedRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { colors, typography } from '../theme';

const maskPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, '');
  return digits ? `••••${digits.slice(-4)}` : '';
};

export const OrderSuccessScreen: React.FC = () => {
  const s = useThemedRTLStyles(getStyles);
  const { t } = useLocale();
  const insets = useSafeAreaInsets();
  const { routeParams } = useAppContext();
  const { params, go } = useStorefront();

  const extra = (key: string): string => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') return new URLSearchParams(window.location.search).get(key) || '';
    return (routeParams as any)?.[key] || '';
  };
  const orderNumber = extra('orderNumber');
  const total = extra('total');
  const notifiedVia = extra('notifiedVia');
  const phone = params.phoneNumber || '';

  const scale = useRef(new Animated.Value(0.6)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.spring(scale, { toValue: 1, friction: 5, tension: 80, useNativeDriver: Platform.OS !== 'web' }),
      Animated.timing(opacity, { toValue: 1, duration: 300, easing: Easing.out(Easing.cubic), useNativeDriver: Platform.OS !== 'web' }),
    ]).start();
  }, [scale, opacity]);

  const channelMessage =
    notifiedVia === 'conversation'
      ? t('storefront.success.sentChat')
      : notifiedVia === 'whatsapp' && phone
        ? t('storefront.success.sentWhatsApp', { phone: maskPhone(phone) })
        : t('storefront.success.body');

  return (
    <View style={s.screen}>
      <ScrollView contentContainerStyle={[s.content, { paddingTop: 48 + insets.top, paddingBottom: 32 + insets.bottom }]}>
        <Animated.View style={[s.iconWrap, { opacity, transform: [{ scale }] }]}>
          <Ionicons name="checkmark" size={56} color={colors.text.onPrimary as string} />
        </Animated.View>

        <Text style={s.title}>{t('storefront.success.title')}</Text>
        {orderNumber ? <Text style={s.orderNumber}>{t('storefront.success.orderNumber', { number: orderNumber })}</Text> : null}

        {total ? (
          <View style={s.totalCard}>
            <Text style={s.totalLabel}>{t('storefront.success.total')}</Text>
            <Text style={s.totalValue}>{total}</Text>
          </View>
        ) : null}

        <View style={s.messageCard}>
          <Ionicons
            name={notifiedVia === 'whatsapp' ? 'logo-whatsapp' : notifiedVia === 'conversation' ? 'chatbubbles-outline' : 'information-circle-outline'}
            size={22}
            color={colors.primary as string}
          />
          <Text style={s.message}>{channelMessage}</Text>
        </View>

        <View style={s.actions}>
          {orderNumber && params.businessId ? (
            <TouchableOpacity style={s.primaryBtn} onPress={() => go('track-order', { orderNumber })} activeOpacity={0.85}>
              <Ionicons name="navigate-outline" size={18} color={colors.text.onPrimary as string} />
              <Text style={s.primaryBtnText}>{t('storefront.success.track')}</Text>
            </TouchableOpacity>
          ) : null}
          {(params.catalogId || params.productId) ? (
            <TouchableOpacity style={s.secondaryBtn} onPress={() => go(params.catalogId ? 'catalog-view' : 'product-view')} activeOpacity={0.85}>
              <Ionicons name="storefront-outline" size={18} color={colors.text.primary as string} />
              <Text style={s.secondaryBtnText}>{t('storefront.success.continue')}</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <Text style={s.poweredBy}>{t('storefront.poweredBy')}</Text>
      </ScrollView>
    </View>
  );
};

const getStyles = () =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: colors.background.secondary,
      ...Platform.select({ web: { width: '100%', maxWidth: '100%', overflowX: 'hidden' as any, height: '100dvh' as any, maxHeight: '100dvh' as any, minHeight: 0 } }),
    } as any,
    content: { flexGrow: 1, alignItems: 'center', paddingHorizontal: 20, width: '100%', maxWidth: 520, alignSelf: 'center' },
    iconWrap: {
      width: 104,
      height: 104,
      borderRadius: 52,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.success,
      marginBottom: 20,
    },
    title: { fontSize: 26, fontFamily: typography.fontFamily.bold, color: colors.text.primary, textAlign: 'center' },
    orderNumber: { fontSize: 15, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary, marginTop: 6, textAlign: 'center' },
    totalCard: {
      marginTop: 20,
      alignSelf: 'stretch',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 16,
      borderRadius: 16,
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    totalLabel: { fontSize: 15, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary },
    totalValue: { fontSize: 20, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    messageCard: {
      marginTop: 12,
      alignSelf: 'stretch',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      padding: 16,
      borderRadius: 16,
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    message: { flex: 1, fontSize: 14, lineHeight: 21, fontFamily: typography.fontFamily.primary, color: colors.text.primary },
    actions: { alignSelf: 'stretch', gap: 10, marginTop: 24 },
    primaryBtn: {
      height: 52,
      borderRadius: 14,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: colors.primary,
    },
    primaryBtnText: { fontSize: 16, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary },
    secondaryBtn: {
      height: 52,
      borderRadius: 14,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.background.surface,
    },
    secondaryBtnText: { fontSize: 15, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    poweredBy: { marginTop: 28, fontSize: 11, color: colors.text.disabled, textAlign: 'center', fontFamily: typography.fontFamily.primary },
  });
